import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useCallData } from '../../contexts/CallDataContext';
import ChatLogs from '../chatHistory/chatLogs';
import ChatDetails from '../chatHistory/chatDetails';

/**
 * Kronk Chat Tab Component
 * 
 * Displays filtered chat history content specifically for Kronk client.
 * Matches the styling of ChatHistory.jsx similar to how CallsTab matches CallHistory.
 */
const KronkChatTab = ({ searchQuery }) => {
  const { data, loading, error } = useCallData();
  const [filteredChats, setFilteredChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  // Filter chats based on search query
  useEffect(() => {
    if (!data || !searchQuery) {
      setFilteredChats([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = data.filter(chat => {
      // Search in chat content, metadata, and other relevant fields
      const searchableText = [
        chat.metadata?.timestamp,
        chat.metadata?.session_id, // Include session ID for search
        chat.analytics?.sentiment_analysis?.trend,
        chat.success_score,
        chat.booking_exists ? 'booking' : '',
        // Add more searchable fields as needed
      ].filter(Boolean).join(' ').toLowerCase(); // Filter out undefined/null values

      return searchableText.includes(query);
    });

    setFilteredChats(filtered);
  }, [data, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon icon="eos-icons:loading" className="text-4xl text-primary animate-spin mb-4" />
          <p className="text-font-secondary">Loading chat history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
          <p className="text-error">Error loading chat history: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!searchQuery) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="flex flex-row gap-4 items-center">
          <Icon icon="ph:chats-fill" className="text-6xl text-bgAccent" />
          <p className="text-font-secondary text-lg">Enter a search term to find chat results.</p>
        </div>
      </div>
    );
  }

  if (filteredChats.length === 0) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="flex flex-row gap-4 items-center">
          <Icon icon="material-symbols:search-off" className="text-8xl text-font-secondary opacity-50" />
          <div className="flex flex-col gap-2 items-start">
            <p className="text-font-secondary text-lg">No chat results found for "{searchQuery}"</p>
            <p className="text-font-secondary">Try using different keywords or check your spelling</p>
          </div>
        </div>
      </div>
    );
  }

  const handleCloseChatDetails = () => {
    setSelectedChat(null);
  };

  return (
    <div className="flex flex-1 gap-2 w-full min-h-0 overflow-hidden flex-col">
      {/* Breadcrumbs - shown when chat details are open */}
      {selectedChat && (
        <div className="flex items-center gap-1 text-font-primary mb-2 flex-shrink-0">
          <button
            onClick={handleCloseChatDetails}
            className="flex items-center justify-center rounded-lg hover:bg-bgAccent transition-colors cursor-pointer"
            title="Go back to chat history"
          >
            <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
          </button>
          <span 
            className="flex items-center justify-center gap-1 group transition-colors cursor-pointer"
            onClick={handleCloseChatDetails}
          >
            <Icon icon="ph:chats-fill" className="text-xl text-font-secondary group-hover:text-primary transition-colors" />
            <span className="text-font-secondary group-hover:text-font-primary transition-colors">Chat History</span>
          </span>
          <Icon icon="mdi:chevron-right" className="text-lg text-font-secondary" />
          <span className="flex items-center justify-center gap-1">
            <Icon icon="material-symbols:info-outline" className="text-2xl text-primary" />
            <span className="text-font-primary text-base">
              Chat Details
            </span>
          </span>
        </div>
      )}

      <div className="flex flex-1 gap-2 w-full min-h-0 overflow-hidden">
        {/* Chat List */}
        <div className={`flex flex-col w-full transition-all duration-300 ease-out overflow-hidden
          ${selectedChat ? 'hidden 2xl:flex 2xl:w-1/2' : 'w-full'}
        `}>
          <ChatLogs
            onRowClick={setSelectedChat}
            isShort={!!selectedChat}
            chatRow={selectedChat?.id}
            displayData={filteredChats}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            className="w-full h-full flex-1 min-h-0"
          />
        </div>

        {/* Chat Details Panel */}
        {selectedChat && (
          <div className="w-full flex flex-col 2xl:w-1/2 flex-shrink-0 min-h-0 max-h-screen overflow-hidden">
            <ChatDetails chat={selectedChat} onClose={handleCloseChatDetails} />
          </div>
        )}
      </div>
    </div>
  );
};

export default KronkChatTab;
